import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { AssignmentTurnedIn as ConsentIcon } from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, FormTextField, FormSelect, FormDatePicker, ChoiceChipGroup, EmptyState } from "../../atoms";
import DrawerField from "../../molecules/DrawerField";
import { tileInputProps } from "../../molecules/InfoTile";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import {
  useListConsentTemplatesMutation,
  useCreateConsentRecordMutation,
  useSendConsentRequestMutation,
} from "../../../store/traditionalApi";
import { splitDateTime, joinDateTime } from "./practiceSessionFormat";

const METHOD_OPTIONS = [
  { value: "paper", label: "Kağıt (ıslak imza)" },
  { value: "mobile", label: "Mobil onaya gönder" },
];

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";

/**
 * Onam kaydı: şablon seç → metni göster → yöntem + imzalayan → kaydet.
 * Metin imza anındaki haliyle sunucuda kopyalanır. "Mobil onaya gönder" formu
 * hastanın telefonuna yollar; hasta adını yazarak onaylayınca kayıt kendiliğinden oluşur.
 *
 * @param {string} [practiceId] - seans editöründen açıldıysa uygulama türü (şablon önerisi)
 * @param {(record) => void} onSaved
 */
export default function ConsentRecordDrawer({ open, onClose, token, patientId, clinicId, patientName, practiceId, onSaved }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const [listTemplates, { isLoading: loadingTemplates }] = useListConsentTemplatesMutation();
  const [createRecord, { isLoading: savingRecord }] = useCreateConsentRecordMutation();
  const [sendRequest, { isLoading: sending }] = useSendConsentRequestMutation();
  const saving = savingRecord || sending;

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("paper");

  useEffect(() => {
    if (!open || !token) return;
    setSignedBy(patientName || "");
    setDate(splitDateTime(new Date()).date);
    setMethod("paper");
    let cancelled = false;
    listTemplates({ token, ...(clinicId ? { clinicId } : {}) })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        const items = (res?.data?.items || []).filter((t) => t.isActive !== false);
        setTemplates(items);
        // Öneri: uygulamaya özel klinik şablonu > özel sistem şablonu > genel klinik > genel sistem
        const score = (t) =>
          (practiceId && idOf(t.practice) === practiceId ? 4 : !idOf(t.practice) ? 1 : 0) + (t.isSystem ? 0 : 2);
        const best = [...items]
          .filter((t) => !idOf(t.practice) || (practiceId && idOf(t.practice) === practiceId))
          .sort((a, b) => score(b) - score(a))[0];
        setTemplateId(best?._id || items[0]?._id || "");
      })
      .catch(() => !cancelled && showError("Onam şablonları yüklenemedi."));
    return () => {
      cancelled = true;
    };
  }, [open, token, clinicId, practiceId, patientName, listTemplates, showError]);

  const template = useMemo(() => templates.find((t) => t._id === templateId) || null, [templates, templateId]);
  const options = useMemo(
    () =>
      templates.map((t) => ({
        value: t._id,
        label: `${t.title}${t.practice?.name ? ` · ${t.practice.name}` : " · Genel"}${t.isSystem ? " (sistem)" : ""}`,
      })),
    [templates]
  );

  const isMobile = method === "mobile";
  const canSubmit = isMobile ? Boolean(template) : Boolean(template && signedBy.trim() && date);

  const handleSave = async () => {
    if (!canSubmit) return;
    if (isMobile) {
      try {
        await sendRequest({ token, patientId, ...(clinicId ? { clinicId } : {}), templateId }).unwrap();
        showSuccess(`Form ${terms.patientGenLower} telefonuna gönderildi; onaylayınca kayıt kendiliğinden oluşur.`);
        onClose?.();
      } catch (e) {
        showError(e?.data?.message || "Form gönderilemedi.");
      }
      return;
    }
    try {
      const res = await createRecord({
        token,
        patientId,
        ...(clinicId ? { clinicId } : {}),
        // Uygulama şablondan gelir; genel şablon genel onam olur (tüm uygulamalar)
        templateId,
        signedAt: joinDateTime(date, splitDateTime(new Date()).time),
        method: "paper",
        signedBy: signedBy.trim(),
      }).unwrap();
      showSuccess("Onam kaydedildi.");
      onSaved?.(res?.data?.record);
      onClose?.();
    } catch (e) {
      showError(e?.data?.message || "Onam kaydedilemedi.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, display: "flex", flexDirection: "column", bgcolor: COLORS.white } }}
    >
      <DrawerHeader icon={<ConsentIcon />} title="Onam Kaydet" subtitle={patientName || `${terms.patient} onamı`} onClose={onClose} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
        {!loadingTemplates && templates.length === 0 ? (
          <EmptyState
            variant="illustrated-sm"
            icon={<ConsentIcon />}
            title="Onam şablonu yok"
            description="Klinik Şablonları › Onam Formları sayfasından bir şablon oluşturun."
          />
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 2.5, columnGap: 2 }}>
            <DrawerField label="Onam şablonu *" fullWidth>
              <FormSelect
                value={templateId}
                onChange={setTemplateId}
                options={options}
                placeholder="Şablon seçin"
                variant="standard"
                size="small"
                fullWidth
                InputProps={tileInputProps(true)}
              />
            </DrawerField>

            {template ? (
              <DrawerField label="Onam metni" fullWidth hint={`Kaydedildiğinde metin bu haliyle ${terms.patientGenLower} kaydına kopyalanır.`}>
                <Box
                  sx={{
                    mt: 0.5,
                    maxHeight: 280,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    bgcolor: COLORS.slate50,
                    border: `1px solid ${COLORS.slate200}`,
                  }}
                >
                  <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, mb: 1 }}>
                    {template.title}
                  </Typography>
                  {String(template.body || "")
                    .split(/\n\s*\n/)
                    .map((p, i) => (
                      <Typography key={i} sx={{ fontSize: TYPO.body, color: COLORS.slate700, mb: 1, whiteSpace: "pre-wrap" }}>
                        {p.trim()}
                      </Typography>
                    ))}
                </Box>
              </DrawerField>
            ) : null}

            <DrawerField
              label="Onay yöntemi"
              fullWidth
              hint={isMobile ? `${terms.patient} metni telefonundan okuyup adını yazarak onaylar; push bildirimi gider.` : undefined}
            >
              <ChoiceChipGroup options={METHOD_OPTIONS} value={method} onChange={setMethod} palette={palette} ariaLabel="Onay yöntemi" sx={{ mt: 0.5 }} />
            </DrawerField>

            {!isMobile && (<>
            <DrawerField label="İmzalayan *">
              <FormTextField
                palette={palette}
                variant="standard"
                size="small"
                fullWidth
                placeholder="Ad Soyad"
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                InputProps={tileInputProps(true)}
              />
            </DrawerField>

            <DrawerField label="İmza tarihi *">
              <FormDatePicker
                value={date}
                onChange={setDate}
                variant="standard"
                fullWidth
                clearable={false}
                maxDate={splitDateTime(new Date()).date}
              />
            </DrawerField>
            </>)}
          </Box>
        )}
      </Box>

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={handleSave}
        submitLabel={isMobile ? "Telefona Gönder" : "Onamı Kaydet"}
        loadingLabel={isMobile ? "Gönderiliyor..." : "Kaydediliyor..."}
        loading={saving}
        disabled={!canSubmit}
        fullWidth
      />
    </Drawer>
  );
}
